import { useEffect } from 'react';
import { Box, Container, Typography, Divider, Paper } from '@mui/material';

/**
 * Página legal pública de Amanda (sin auth): política de privacidad,
 * condiciones del servicio y eliminación de datos.
 * Ruta: /privacidad — anclas #condiciones y #eliminacion (las 3 URLs que
 * exige la app de Meta para pasar a modo Activo).
 *
 * El texto está en español y hardcodeado a propósito (documento legal:
 * no debe variar con el idioma de la interfaz). Revisar con un abogado
 * antes del lanzamiento comercial.
 */
const CONTACT_EMAIL = 'david@amandahr.ai'; // ← correo de contacto para datos personales
const LAST_UPDATE = '13 de agosto de 2026';

const H = ({ children, id }) => (
  <Typography id={id} variant="h5" fontWeight={700} sx={{ mt: 5, mb: 2, color: '#1F2937', scrollMarginTop: 24 }}>
    {children}
  </Typography>
);

const S = ({ children }) => (
  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 1, color: '#374151' }}>
    {children}
  </Typography>
);

const P = ({ children }) => (
  <Typography variant="body2" sx={{ mb: 1.5, color: '#4B5563', lineHeight: 1.7 }}>
    {children}
  </Typography>
);

const PrivacyPolicy = () => {
  // Scroll al ancla si la URL trae hash (#condiciones / #eliminacion)
  useEffect(() => {
    const hash = window.location.hash?.replace('#', '');
    if (hash) {
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F9FAFB', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid #E5E7EB', borderRadius: 3 }}>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#1F2937' }}>
            Amanda — Privacidad y Condiciones
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Última actualización: {LAST_UPDATE}
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* ── 1. POLÍTICA DE PRIVACIDAD ── */}
          <H id="privacidad">Política de Privacidad</H>
          <P>
            Amanda es una plataforma de reclutamiento y selección de personas operada por la consultora
            responsable del proceso al que postulas (en adelante, "la Consultora"). Esta política explica
            qué datos personales tratamos cuando participas en un proceso de postulación — por ejemplo, al
            conversar con nuestro asistente por WhatsApp o por el chat del sitio web — y cuáles son tus
            derechos, conforme a la Ley N° 19.628 sobre Protección de la Vida Privada y la Ley N° 21.719
            de Protección de Datos Personales de Chile.
          </P>

          <S>Qué datos recopilamos</S>
          <P>
            Durante la conversación de postulación recopilamos los datos que tú mismo entregas: datos de
            identificación (nombre, apellidos, RUT o documento de identidad, nacionalidad), datos de
            contacto (teléfono, correo electrónico, comuna de residencia), antecedentes laborales
            (experiencia, expectativa de renta, disponibilidad) y los documentos que decidas adjuntar
            (currículum y, cuando el proceso lo requiera, documento de identidad, visa o permiso de
            trabajo). Solo almacenamos archivos que el asistente te haya solicitado expresamente.
          </P>

          <S>Para qué los usamos</S>
          <P>
            Los datos se usan para gestionar tu postulación: contactarte, evaluar tu adecuación al cargo,
            responder tus dudas sobre la vacante y avanzar en las etapas del proceso de selección. Si
            aceptas quedar registrado/a en nuestra base de talentos, también podremos contactarte para
            futuras oportunidades laborales compatibles con tu perfil.
          </P>

          <S>Verificación de antecedentes</S>
          <P>
            Para ciertos cargos, la Consultora puede consultar antecedentes a través de proveedores
            externos de verificación, utilizando los datos de identificación que entregaste. Estas
            consultas se basan en fuentes públicas; sus resultados se revisan siempre por una persona del
            equipo antes de incidir en el proceso, considerando el riesgo de homonimia.
          </P>

          <S>Con quién compartimos tus datos</S>
          <P>
            Tus datos son tratados por la Consultora y compartidos con la empresa mandante del proceso al
            que postulas, exclusivamente para fines de selección. También usamos proveedores tecnológicos
            que dan soporte a la plataforma (alojamiento, mensajería de WhatsApp a través de Meta,
            verificación de antecedentes), quienes tratan los datos por cuenta de la Consultora. No
            vendemos tus datos personales a terceros.
          </P>

          <S>Cuánto tiempo los conservamos</S>
          <P>
            Conservamos tus datos mientras dure el proceso de selección y, si aceptaste quedar en la base
            de talentos, hasta que solicites tu eliminación. Puedes pedir la eliminación de tus datos en
            cualquier momento (ver sección "Eliminación de datos").
          </P>

          <S>Tus derechos</S>
          <P>
            Puedes ejercer en cualquier momento tus derechos de acceso, rectificación, cancelación,
            oposición y portabilidad escribiendo a {CONTACT_EMAIL}. Responderemos dentro de los plazos
            legales. Si consideras que tus derechos no han sido respetados, puedes reclamar ante la
            autoridad de protección de datos competente.
          </P>

          <Divider sx={{ my: 4 }} />

          {/* ── 2. CONDICIONES DEL SERVICIO ── */}
          <H id="condiciones">Condiciones del Servicio</H>
          <P>
            Al usar el asistente de Amanda (por WhatsApp o por el chat web) aceptas estas condiciones:
          </P>
          <P>
            <strong>Uso del servicio.</strong> El asistente existe para gestionar postulaciones a ofertas
            laborales y responder dudas sobre ellas. Participar en una conversación de postulación es
            gratuito y voluntario; puedes abandonarla en cualquier momento.
          </P>
          <P>
            <strong>Veracidad de la información.</strong> Te comprometes a entregar información veraz y
            propia. Entregar datos de terceros o información falsa puede terminar el proceso de
            postulación.
          </P>
          <P>
            <strong>Sin garantía de contratación.</strong> Postular a través de Amanda no garantiza
            entrevistas ni contratación; las decisiones de selección corresponden a la Consultora y a la
            empresa mandante.
          </P>
          <P>
            <strong>Respeto.</strong> Las conversaciones deben mantenerse dentro de un marco de respeto.
            El uso de lenguaje ofensivo puede terminar la conversación y suspender el acceso al canal.
          </P>
          <P>
            <strong>Cambios.</strong> Estas condiciones pueden actualizarse; la versión vigente estará
            siempre disponible en esta página.
          </P>

          <Divider sx={{ my: 4 }} />

          {/* ── 3. ELIMINACIÓN DE DATOS ── */}
          <H id="eliminacion">Eliminación de datos</H>
          <P>
            Puedes solicitar la eliminación de tus datos personales de Amanda en cualquier momento,
            enviando un correo a <strong>{CONTACT_EMAIL}</strong> con el asunto "Eliminación de datos",
            indicando tu nombre y tu RUT o documento de identidad (para poder identificar tu registro).
          </P>
          <P>
            Procesaremos tu solicitud dentro de un plazo máximo de 15 días hábiles. La eliminación
            comprende tus datos de identificación y contacto, tus respuestas de postulación, los
            documentos que adjuntaste y tu registro en la base de talentos. Ten presente que podremos
            conservar la información mínima necesaria cuando exista una obligación legal que lo exija.
          </P>
          <P>
            Si nos escribiste por WhatsApp, también puedes pedir dentro de la misma conversación que no te
            volvamos a contactar por ese canal.
          </P>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
